/*
 * Nilya Press — coil geometry and quoting.
 *
 * Ported verbatim from the Next.js build (src/data/coil.ts, src/data/pricing.ts).
 * Nothing here touches the DOM or a framework: the estimator section and any
 * future section both read from this one file, so two parts of the site cannot
 * quote different numbers.
 *
 * A 4:1 pitch coil punches four holes to the inch. Coil diameter is NOT the
 * block thickness — it is the next stock size up from thickness plus about an
 * eighth of an inch of clearance, because a coil sized exactly to the block
 * cannot turn.
 */
(function (global) {
  'use strict';

  var PITCH_IN = 0.25; // 4:1
  var CLEARANCE_IN = 0.125;
  var SPINE_TRIM_MM = 3; // the glued strip that comes off
  var PUNCH_INSET_MM = 7; // hole centres, in from the new edge
  var MIN_INNER_MARGIN_IN = 0.5; // tighter than this and the punch eats text

  /* The diameters we stock. */
  var coilSizes = [
    { in: 0.25, label: '1/4"', mm: 6 },
    { in: 0.3125, label: '5/16"', mm: 8 },
    { in: 0.375, label: '3/8"', mm: 10 },
    { in: 0.4375, label: '7/16"', mm: 11 },
    { in: 0.5625, label: '9/16"', mm: 14 },
    { in: 0.625, label: '5/8"', mm: 16 },
    { in: 0.6875, label: '11/16"', mm: 18 },
    { in: 0.75, label: '3/4"', mm: 20 },
    { in: 0.875, label: '7/8"', mm: 22 },
    { in: 1, label: '1"', mm: 25 },
    { in: 1.125, label: '1 1/8"', mm: 28 },
    { in: 1.25, label: '1 1/4"', mm: 32 },
    { in: 1.5, label: '1 1/2"', mm: 38 },
    { in: 1.75, label: '1 3/4"', mm: 45 },
    { in: 2, label: '2"', mm: 50 }
  ];

  var LARGEST_COIL = coilSizes[coilSizes.length - 1];

  /*
   * The thickest block we can bind, derived rather than declared: the largest
   * coil we stock, less the clearance it needs to turn. 1.875", not a round 2".
   */
  var MAX_THICKNESS_IN = LARGEST_COIL.in - CLEARANCE_IN;

  /* The coil a block of this thickness takes, or null if we cannot bind it. */
  function coilFor(thicknessIn) {
    var needed = thicknessIn + CLEARANCE_IN;
    for (var i = 0; i < coilSizes.length; i++) {
      if (coilSizes[i].in >= needed) return coilSizes[i];
    }
    return null;
  }

  /*
   * ───────────────────────────────────────────────────────────────────────
   *  UNVERIFIED. Every figure below is a placeholder.
   *
   *  These bands were derived from competitors' published pricing in
   *  September 2026 — NOT from Nilya's costs. Replace them with real numbers
   *  before the store takes a real order.
   * ───────────────────────────────────────────────────────────────────────
   */

  /* Bands, not a per-inch rate: a thick block takes more punch passes, and a
     stepped price is easier to say out loud than a formula. */
  var BIND_BANDS = [
    { upTo: 0.5, price: 14 },
    { upTo: 1, price: 18 },
    { upTo: 1.5, price: 23 },
    { upTo: MAX_THICKNESS_IN, price: 29 }
  ];

  var options = [
    { id: 'clear', label: 'Clear front cover', price: 2.5 },
    { id: 'board', label: 'Rigid back board', price: 2 },
    { id: 'laminate', label: 'Laminate the original cover', price: 6 }
  ];

  var SHIP_FIRST = 8.95;
  var SHIP_EACH_EXTRA = 2.5;

  function volumeRate(qty) {
    if (qty >= 10) return 0.25;
    if (qty >= 5) return 0.18;
    if (qty >= 2) return 0.1;
    return 0;
  }

  function quote(thicknessIn, qty, chosen) {
    chosen = chosen || [];
    var coil = coilFor(thicknessIn);

    var bindEach = BIND_BANDS[BIND_BANDS.length - 1].price;
    for (var i = 0; i < BIND_BANDS.length; i++) {
      if (thicknessIn <= BIND_BANDS[i].upTo) {
        bindEach = BIND_BANDS[i].price;
        break;
      }
    }

    var optionsEach = 0;
    for (var j = 0; j < options.length; j++) {
      if (chosen.indexOf(options[j].id) !== -1) optionsEach += options[j].price;
    }

    var bindTotal = bindEach * qty;
    var optionsTotal = optionsEach * qty;
    var discountRate = volumeRate(qty);
    var discount = (bindTotal + optionsTotal) * discountRate;
    var shipping = SHIP_FIRST + Math.max(0, qty - 1) * SHIP_EACH_EXTRA;
    var total = bindTotal + optionsTotal - discount + shipping;

    return {
      bindable: coil !== null,
      coilLabel: coil ? coil.label : '—',
      coilMm: coil ? coil.mm : null,
      bindEach: bindEach,
      bindTotal: bindTotal,
      optionsEach: optionsEach,
      optionsTotal: optionsTotal,
      discountRate: discountRate,
      discount: discount,
      shipping: shipping,
      total: total,
      perBook: total / qty
    };
  }

  function money(value) {
    return '$' + value.toFixed(2);
  }

  /* The single example the homepage quotes, so the band and the estimator agree. */
  var EXAMPLE_THICKNESS_IN = 0.9;

  global.NilyaBinding = {
    PITCH_IN: PITCH_IN,
    CLEARANCE_IN: CLEARANCE_IN,
    SPINE_TRIM_MM: SPINE_TRIM_MM,
    PUNCH_INSET_MM: PUNCH_INSET_MM,
    MIN_INNER_MARGIN_IN: MIN_INNER_MARGIN_IN,
    MAX_THICKNESS_IN: MAX_THICKNESS_IN,
    EXAMPLE_THICKNESS_IN: EXAMPLE_THICKNESS_IN,
    coilSizes: coilSizes,
    options: options,
    coilFor: coilFor,
    quote: quote,
    money: money
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = global.NilyaBinding;
})(typeof window !== 'undefined' ? window : globalThis);
